import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import tokenManager from '../utils/tokenManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));

  const initializeAuth = useCallback(async () => {
    setAuthError(null);
    if (token) {
      try {
        const userData = await authAPI.getCurrentUser();
        console.log('User data received:', userData);
        setUser(userData);
        
        // Start proactive token refresh for authenticated users
        tokenManager.startProactiveRefresh();
      } catch (error) {
        console.error('Failed to get current user:', error);
        
        // Determine if this is a connection/timeout error vs auth error
        const isConnectionError = !error.response || error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        
        if (isConnectionError) {
          // Connection failed - show error with retry option
          const errorMessage = error.code === 'ECONNABORTED' 
            ? 'Connection timed out. Server may be slow or unavailable.'
            : 'Unable to connect to server. Please check your connection.';
          setAuthError(errorMessage);
        } else {
          // Auth error (401, etc.) - clear tokens and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setToken(null);
          setRefreshToken(null);
          tokenManager.stopProactiveRefresh();
        }
      }
    } else {
      // Stop proactive refresh if no token
      tokenManager.stopProactiveRefresh();
    }
    setLoading(false);
  }, [token]);

  const retryAuth = useCallback(async () => {
    setLoading(true);
    setAuthError(null);
    await initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Listen for token refresh events
  useEffect(() => {
    const handleTokenRefreshed = (event) => {
      console.log('🔄 Token refreshed by TokenManager');
      setToken(event.detail.access_token);
    };

    const handleTokenRefreshFailed = (event) => {
      console.error('🔄 Token refresh failed:', event.detail.error);
      // Let the reactive system handle the failure
    };

    window.addEventListener('tokenRefreshed', handleTokenRefreshed);
    window.addEventListener('tokenRefreshFailed', handleTokenRefreshFailed);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefreshed);
      window.removeEventListener('tokenRefreshFailed', handleTokenRefreshFailed);
    };
  }, []);

  const login = async (email, password) => {
    try {
      console.log('🔍 Starting login process...');
      const response = await authAPI.login(email, password);
      console.log('🔍 Login API response:', JSON.stringify(response, null, 2));
      
      const { access_token, refresh_token } = response;
      console.log('🔍 Tokens extracted:', { 
        access_token: access_token ? 'Yes' : 'No',
        refresh_token: refresh_token ? 'Yes' : 'No'
      });
      
      if (!access_token || !refresh_token) {
        throw new Error('No tokens received from server');
      }
      
      console.log('🔍 Storing tokens in localStorage...');
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      setToken(access_token);
      setRefreshToken(refresh_token);
      
      // Add a small delay to ensure token is properly set
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        const userData = await authAPI.getCurrentUser();
        console.log('User data after login:', userData);
        setUser(userData);
        
        // Start proactive token refresh after successful login
        tokenManager.startProactiveRefresh();
        
        return { success: true };
      } catch (userError) {
        console.error('Failed to get user data:', userError);
        // Even if user data fetch fails, login was successful
        // Start proactive refresh anyway since we have tokens
        tokenManager.startProactiveRefresh();
        return { success: true, warning: 'Login successful but user data could not be loaded' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      // Handle different error response structures
      let errorMessage = 'Login failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err => err.msg || err.message || 'Validation error').join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, user: response };
    } catch (error) {
      console.error('Registration failed:', error);
      
      // Handle different error response structures
      let errorMessage = 'Registration failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err => err.msg || err.message || 'Validation error').join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout API to revoke refresh token
      if (refreshToken) {
        await authAPI.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    }
    
    // Stop proactive token refresh
    tokenManager.stopProactiveRefresh();
    
    // Clear local storage and state
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    authError,
    login,
    register,
    logout,
    retryAuth,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 